import React, { useState } from "react";
import styled from "styled-components";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import logo from "./Assets/logo.png";

const Main = styled.nav`
  position: fixed;
  top: 0;
  left: 0;

  width: 100%;
  height: 76px;

  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;

  padding: 0 clamp(16px,4vw,60px);

  background: rgba(255,255,255,.9);
  backdrop-filter: blur(18px);

  border-bottom:1px solid rgba(15,23,42,.08);

  z-index:1000;

  @media(max-width:900px){
      grid-template-columns:auto 1fr auto;
      gap:10px;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 14px;
  text-decoration: none;
`;

const LogoImage = styled.img`
  width: 44px;
  height: 44px;
  object-fit: contain;
`;

const Head = styled.h2`
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.5px;

  @media (max-width: 500px) {
    font-size:1.15rem;
  }
`;

const NavLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 46px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const StyledLink = styled(NavLink)`
  position: relative;
  text-decoration: none;

  color: #475569;

  font-size: 15px;

  font-weight: 600;

  transition: all 0.25s ease;

  &.active {
    color: #2563eb;
  }

  &:hover {
    color: #2563eb;
  }

  &::after {
    content: "";

    position: absolute;

    left: 0;
    bottom: -10px;

    width: 0;

    height: 2px;

    border-radius: 50px;

    background: #2563eb;

    transition: 0.3s;
  }

  &:hover::after,
  &.active::after {
    width: 100%;
  }
`;

const DesktopActions = styled.div`
  display: flex;
  align-items: center;

  margin-right: clamp(0px, 2vw, 28px);

 @media(max-width:900px){
        justify-self:end;
        margin-right: 0;
    }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-height: 46px;

  padding: 0 22px;

  border: none;

  border-radius: 14px;

  background: #2563eb;

  color: white;

  font-size: 15px;

  font-weight: 600;

  cursor: pointer;

  white-space: nowrap;

  transition: 0.3s;

  box-shadow: 0 10px 25px rgba(37, 99, 235, 0.22);

  &:hover {
    background: #1d4ed8;

    transform: translateY(-2px);

    box-shadow: 0 18px 35px rgba(37, 99, 235, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 400px) {
    min-height: 42px;
    padding: 0 16px;
    font-size: 14px;
  }
`;

const MenuButton = styled.button`
  display: none;

  width: 60px;
  height: 60px;

  border: none;

  border-radius: 12px;

  background: transparent;

  cursor: pointer;

  color: #0f172a;

  transition: 0.3s;

  &:hover {
    background: #f1f5f9;
  }

  @media (max-width: 900px) {
    display: flex;
    align-items: center;
    justify-self:start;
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;

  background: rgba(15, 23, 42, 0.35);

  backdrop-filter: blur(3px);

  z-index: 998;
`;

const MobileDrawer = styled(motion.div)`
  position: fixed;

  top: 76px;
  left: 0;

  width: 300px;
  max-width: 85vw;

  height: calc(100vh - 76px);

  background: white;

  display: flex;
  flex-direction: column;

  padding: 30px 24px;

  gap: 8px;

  border-right: 1px solid #e5e7eb;

  box-shadow: 25px 0 60px rgba(15, 23, 42, 0.08);

  z-index: 999;
`;

const MobileLink = styled(StyledLink)`
  padding: 16px 0;

  font-size: 16px;

  border-bottom: 1px solid #f1f5f9;

  &::after {
    display: none;
  }
`;
const MobileHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  padding-bottom: 24px;
  margin-bottom: 16px;

  border-bottom: 1px solid #e5e7eb;
`;

const MobileLogo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;

  text-decoration: none;
`;

const MobileTitle = styled.h2`
  margin: 0;

  font-size: 1.4rem;
  font-weight: 700;

  color: #0f172a;
`;

const MobileLogin = styled(Link)`
  text-decoration: none;
`;

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <Main>
        <Logo to="/" onClick={closeMenu}>
          <LogoImage src={logo} alt="AquaTools Logo" />
          <Head>AQUA TOOLS</Head>
        </Logo>

        <NavLinks>
          <StyledLink to="/">Home</StyledLink>

          <StyledLink to="/about">About</StyledLink>

          <StyledLink to="/products">Products</StyledLink>

          <StyledLink to="/portfolio">Portfolio</StyledLink>

          <StyledLink to="/contactus">Contact</StyledLink>
        </NavLinks>

        <DesktopActions>
          <Link
            to="/login"
            style={{ textDecoration: "none" }}
          >
            <Button>Login</Button>
          </Link>
        </DesktopActions>

        <MenuButton onClick={() => setMenuOpen(!menuOpen)}>
          <AnimatePresence mode="wait">
            {menuOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <CloseIcon fontSize="medium" />
              </motion.div>
            ) : (
              <motion.div
                key="menu"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <MenuIcon fontSize="medium" />
              </motion.div>
            )}
          </AnimatePresence>
        </MenuButton>
        
      </Main>

      <AnimatePresence>
        {menuOpen && (
          <>
            <Overlay
              onClick={closeMenu}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />

           <MobileDrawer
  initial={{ x: -320 }}
  animate={{ x: 0 }}
  exit={{ x: -320 }}
  transition={{
    duration: 0.35,
    ease: "easeOut",
  }}
>
  <MobileHeader>
    <MobileLogo to="/" onClick={closeMenu}>
      <LogoImage src={logo} alt="AquaTools" />
      <MobileTitle>AquaTools</MobileTitle>
    </MobileLogo>

    <MobileLogin to="/login" onClick={closeMenu}>
      <Button
        style={{
          width: "100%",
        }}
      >
        Login
      </Button>
    </MobileLogin>
  </MobileHeader>

  <MobileLink to="/" onClick={closeMenu}>
    Home
  </MobileLink>

  <MobileLink to="/about" onClick={closeMenu}>
    About
  </MobileLink>

  <MobileLink to="/products" onClick={closeMenu}>
    Products
  </MobileLink>

  <MobileLink to="/portfolio" onClick={closeMenu}>
    Portfolio
  </MobileLink>

  <MobileLink to="/contactus" onClick={closeMenu}>
    Contact
  </MobileLink>
</MobileDrawer>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;